import { useCallback } from 'react';
import { RenderIf, Spinner } from 'react-rainbow-components';
import { hideAppSpinner, showAppMessage, showAppSpinner } from '@rainbow-modules/app';
import usePhoneNumber from 'data/firestore/phonenumber/useOnce';
import { Phonenumber } from 'data/firestore/phonenumber/types';
import useHttpMutation from 'data/firestore/useHttpMutation';
import FormFooter from './footer';
import Form from './form';
import { Container, Content } from './styled';
import { BaseFormProps } from './types';

const mapDataToFormValues = (
    phoneNumberInfo?: Phonenumber | null,
) => {
    if (phoneNumberInfo) {
        const {
            language,
            voicePipelineServer,
        } = phoneNumberInfo;

        return {
            language,
            pipeline: voicePipelineServer?.id,
            params: voicePipelineServer?.params,
        } as Record<string, unknown>;
    }

    return undefined;
};

interface EditFormProps extends BaseFormProps {}

const EditPhoneNumberForm = ({
    agentId = '',
    onRequestClose = () => {},
    onCompleted = () => {},
    ...phoneNumberInfo
}: EditFormProps) => {
    const { data: phoneNumberData, isLoading } = usePhoneNumber(
        phoneNumberInfo?.id as string,
        { disabled: !phoneNumberInfo?.id },
    );
    const {
        mutateAsync: updatePhoneNumber,
    } = useHttpMutation<Record<string, unknown>, void>({
        pathname: `/agents/${agentId}/phone-numbers/${phoneNumberData?.id}`,
        method: 'PATCH',
        onSuccess: (response) => {
            onRequestClose();
            onCompleted(response as unknown as Record<string, unknown>);
        },
    });

    const handleSubmit = useCallback(async ({
        voicePipelineServer,
        language,
    }: Record<string, unknown>) => {
        try {
            showAppSpinner();
            await updatePhoneNumber({
                body: {
                    voicePipelineServer,
                    language,
                },
            });
        } catch (error) {
            // no catch
            showAppMessage({
                variant: 'error',
                message: (error as Error).message,
                timeout: 5000,
            });
        }
        hideAppSpinner();
    }, [updatePhoneNumber]);

    return (
        <Container>
            <Content>
                <RenderIf isTrue={isLoading}>
                    <Spinner size="large" variant="brand" type="arc" />
                </RenderIf>
                <RenderIf isTrue={!isLoading}>
                    <Form
                        mode="edit"
                        agentId={agentId}
                        disabledFields={['provider', 'phoneNumber']}
                        id="phone-number-form"
                        initialValues={mapDataToFormValues(phoneNumberData)}
                        onSubmit={handleSubmit}
                        phoneNumber={phoneNumberInfo?.number}
                    />
                </RenderIf>
            </Content>
            <FormFooter
                formId="phone-number-form"
                submitDisabled={isLoading}
                submitButtonLabel="Update"
                onCancelClick={onRequestClose}
            />
        </Container>
    );
};

export default EditPhoneNumberForm;
